// Client-only protocol annex — types and helpers munch owns that are NOT part of belch's
// authoritative wire contract (src/types.ts → generated/server.ts). The serialized offer
// forms (bigint-as-string), their deserializer, and munch-side view models live here. The
// generated wire types import SerializedBattleOffer from this file; that types-only cycle is safe.
import { Hex } from 'viem';
import type { Battle, BattleOffer } from '../generated/server';

// Serialized types with string (received from server after JSON serialization)
// BigInt values are serialized as strings for JSON transport
export interface SerializedBattle {
  p0: Hex;
  p0TeamIndex: string;
  p1: Hex;
  p1TeamIndex: string;
  p2: Hex;
  p2TeamIndex: string;
  p3: Hex;
  p3TeamIndex: string;
  teamRegistry: Hex;
  rngOracle: Hex;
  ruleset: Hex;
  moveManager: Hex;
  matchmaker: Hex;
  engineHooks: Hex[];
}

export interface SerializedBattleOffer {
  battle: SerializedBattle;
  pairHashNonce: string;
  battleMode: number;
}

export function deserializeBattleOffer(serialized: SerializedBattleOffer): BattleOffer {
  return {
    battle: {
      p0: serialized.battle.p0,
      p0TeamIndex: BigInt(serialized.battle.p0TeamIndex),
      p1: serialized.battle.p1,
      p1TeamIndex: BigInt(serialized.battle.p1TeamIndex),
      p2: serialized.battle.p2,
      p2TeamIndex: BigInt(serialized.battle.p2TeamIndex),
      p3: serialized.battle.p3,
      p3TeamIndex: BigInt(serialized.battle.p3TeamIndex),
      teamRegistry: serialized.battle.teamRegistry,
      rngOracle: serialized.battle.rngOracle,
      ruleset: serialized.battle.ruleset,
      moveManager: serialized.battle.moveManager,
      matchmaker: serialized.battle.matchmaker,
      engineHooks: serialized.battle.engineHooks,
    },
    pairHashNonce: BigInt(serialized.pairHashNonce),
    battleMode: serialized.battleMode,
  };
}

/**
 * Internal shape of a client in `ServerService.clients`. `username` is the
 * only field the wire currently provides; `wins` / `losses` are plumbed as
 * optional so the template can guard on them and start rendering as soon
 * as the server payload grows to include them.
 */
export interface OnlineUser {
  username: string;
  wins?: number;
  losses?: number;
  twitterHandle?: string | null;
}

export interface BattleDataResponsePayload {
  battleKey: Hex;
}

// Recent Battles bar (home) — served by GET /battles/recent. Sides are oriented
// winner/loser by the server; CPU participants have username === null + isCpu.
export interface RecentBattleSide {
  address: Hex;
  username: string | null;
  twitterHandle: string | null;
  isCpu: boolean;
  monIds: number[];
}

export interface RecentBattleEntry {
  battleKey: Hex;
  completedAt: number; // epoch ms
  turnCount: number | null;
  cpuDifficulty: 'easy' | 'medium' | 'hard' | null; // null for pvp
  mode: 'singles' | 'doubles' | 'multi';
  winner: RecentBattleSide;
  loser: RecentBattleSide;
}

export interface RecentBattlesPayload {
  network: 'testnet' | 'mainnet';
  battles: RecentBattleEntry[];
}
